import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '../index'

interface FeatureFlag {
  key: string
  value: boolean | string
  type: string
  description: string
}

interface IntegrationStats {
  totalIntegrations: number
  activeIntegrations: number
  totalImportedProjects: number
  integrationsByType: Array<{ type: string; count: number }>
  recentImports: Array<{
    uuid: string
    import_type: string
    status: string
    records_found: number
    records_imported: number
    records_failed: number
    started_at: string
    completed_at: string
    project_name: string
    integration_type: string
  }>
}

interface Integration {
  id: number
  uuid: string
  organization_id: number
  organization_name: string
  organization_uuid: string
  integration_type: string
  integration_method: string
  status: string
  uses_timelines: boolean
  uses_dependencies: boolean
  last_sync_at: string | null
  project_count: number
  member_count: number
  created_at: string
}

export const integrationsApi = createApi({
  reducerPath: 'integrationsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: (import.meta as any).env?.VITE_API_URL || 'http://localhost:4001/api/v1/admin',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['FeatureFlags', 'IntegrationStats', 'Integrations'],
  endpoints: (builder) => ({
    getFeatureFlags: builder.query<FeatureFlag[], void>({
      query: () => '/integrations/feature-flags',
      transformResponse: (response: any) => response.data,
      providesTags: ['FeatureFlags'],
    }),

    updateFeatureFlag: builder.mutation<any, { key: string; value: boolean }>({
      query: (body) => ({
        url: '/integrations/feature-flags',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['FeatureFlags'],
    }),

    getIntegrationStats: builder.query<IntegrationStats, void>({
      query: () => '/integrations/stats',
      transformResponse: (response: any) => response.data,
      providesTags: ['IntegrationStats'],
    }),

    getIntegrations: builder.query<Integration[], void>({
      query: () => '/integrations',
      transformResponse: (response: any) => response.data,
      providesTags: ['Integrations'],
    }),

    getSourceSelectionStats: builder.query<any, void>({
      query: () => '/integrations/source-selection-stats',
      transformResponse: (response: any) => response.data,
    }),

    getMoSourceConnections: builder.query<any[], void>({
      query: () => '/integrations/mo-source-connections',
      transformResponse: (response: any) => response.data,
    }),
  }),
})

export const {
  useGetFeatureFlagsQuery,
  useUpdateFeatureFlagMutation,
  useGetIntegrationStatsQuery,
  useGetIntegrationsQuery,
  useGetSourceSelectionStatsQuery,
  useGetMoSourceConnectionsQuery,
} = integrationsApi
